import clsx from "classnames";
import { Col, Container, Row, FormControl, FormGroup, FormLabel, Stack, Dropdown } from "react-bootstrap";
import PropTypes from "prop-types";
import classes from "./styles.module.scss";
import { PromoItem } from "@/ui";
import { useMediaQuery } from "@react-hook/media-query";
import { career_advice_helper } from "@/helpers/career_advice_helper";
import { trimText } from "@/functions/trimText";
import { getRoute } from "@/getters/getRoute";
import styles from "./styles.module.scss";
import { useState, useEffect } from "react";

export default function CareerAdviceContent({
  className = "pt-4 pt-md-5",
  items = [],
  visibleCount: desktopVisibleCount = 4,
  itemsCount = 16,
  mobileCount = 4,
}) {
  const [visibleCount, setVisibleCount] = useState(2);
  const xl = useMediaQuery("only screen and (min-width: 1200px)");
  const lg = useMediaQuery("only screen and (min-width: 992px)");
  const md = useMediaQuery("only screen and (min-width: 768px)");

  useEffect(() => {
    setVisibleCount(xl ? desktopVisibleCount : lg ? 3 : md ? 2 : 1);
  }, [xl, lg, md, desktopVisibleCount]);

  const [selectTopicButtonActive, setSelectTopicButtonActive] = useState(false);
  const [selectTopic, setSelectTopic] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([""]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeIndexJobDescriptions, setActiveIndexJobDescriptions] = useState(0);
  const [activeIndexCvTemplates, setActiveIndexCvTemplates] = useState(0);
  const [activeIndexProgression, setActiveIndexProgression] = useState(0);
  const [activeIndexSupport, setActiveIndexSupport] = useState(0);
  const [activeIndexRights, setActiveIndexRights] = useState(0);

  const allItems = career_advice_helper.fetch();
  const categories = ["CV Templates", "Career Progression", "Career Support", "Employment Rights", "Job Descriptions"];

  useEffect(() => {
    const newSearchResult = allItems.filter((item) => item.title.toLowerCase().includes(searchInput.toLowerCase()));
    setSearchResults(newSearchResult);
  }, [searchInput, allItems]);

  const oneItemPerCategory = categories
    .map((category) => {
      const itemsInCategory = allItems.filter((item) => item.related_category === category);
      const sortedItems = itemsInCategory.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      return sortedItems[0];
    })
    .filter(Boolean);

  const handleNavClick = (direction) => {
    setActiveIndex((prevIndex) => {
      if (direction === "prev") {
        return prevIndex > 0 ? prevIndex - 1 : oneItemPerCategory.length - 1;
      } else {
        return prevIndex < oneItemPerCategory.length - 1 ? prevIndex + 1 : 0;
      }
    });
  };

  const handleNavClickJobDescriptions = (direction) => {
    setActiveIndexJobDescriptions((prevIndex) => {
      if (direction === "prev") {
        return prevIndex > 0 ? prevIndex - visibleCount : allCareerJobDescriptionsItems.length - visibleCount;
      } else {
        return prevIndex < allCareerJobDescriptionsItems.length - visibleCount ? prevIndex + visibleCount : 0;
      }
    });
  };

  const handleNavClickCvTemplates = (direction) => {
    setActiveIndexCvTemplates((prevIndex) => {
      if (direction === "prev") {
        return prevIndex > 0 ? prevIndex - visibleCount : allCareerProgressionItems.length - visibleCount;
      } else {
        return prevIndex < allCareerProgressionItems.length - visibleCount ? prevIndex + visibleCount : 0;
      }
    });
  };

  const handleNavClickProgression = (direction) => {
    setActiveIndexProgression((prevIndex) => {
      if (direction === "prev") {
        return prevIndex > 0 ? prevIndex - visibleCount : allCareerProgressionItems.length - visibleCount;
      } else {
        return prevIndex < allCareerProgressionItems.length - visibleCount ? prevIndex + visibleCount : 0;
      }
    });
  };

  const handleNavClickSupport = (direction) => {
    setActiveIndexSupport((prevIndex) => {
      if (direction === "prev") {
        return prevIndex > 0 ? prevIndex - visibleCount : allCareerSupportItems.length - visibleCount;
      } else {
        return prevIndex < allCareerSupportItems.length - visibleCount ? prevIndex + visibleCount : 0;
      }
    });
  };

  const handleNavClickRights = (direction) => {
    setActiveIndexRights((prevIndex) => {
      if (direction === "prev") {
        return prevIndex > 0 ? prevIndex - visibleCount : allEmploymentRightsItems.length - visibleCount;
      } else {
        return prevIndex < allEmploymentRightsItems.length - visibleCount ? prevIndex + visibleCount : 0;
      }
    });
  };

  const hasCategory = (item, target) => {
    const raw = item.related_category;

    const category = Array.isArray(raw)
      ? raw.join(" ")
      : typeof raw === "object" && raw !== null
      ? raw.name || raw.label || raw.value || ""
      : raw || "";

    return category.toLowerCase().includes(target.toLowerCase());
  };

  const getDateValue = (item) => {
    if (!item.date) return 0;
    const parsed = new Date(item.date);
    return isNaN(parsed) ? 0 : parsed.getTime();
  };

  const allCareerJobDescriptionsItems = career_advice_helper
    .fetch({ limit: 0, featured: false })
    .filter((item) => hasCategory(item, "Job Descriptions"))
    .sort((a, b) => getDateValue(b) - getDateValue(a))
    .slice(0, md ? itemsCount : mobileCount);

  const CareerJobDescriptionsItemsToShow = allCareerJobDescriptionsItems.slice(
    activeIndexJobDescriptions,
    activeIndexJobDescriptions + visibleCount,
  );

  const allCareerCvTemplatesItems = career_advice_helper
    .fetch({ limit: 0, featured: false })
    .filter((item) => hasCategory(item, "CV Templates"))
    .sort((a, b) => getDateValue(b) - getDateValue(a))
    .slice(0, md ? itemsCount : mobileCount);

  const CareerCvTemplatesItemsToShow = allCareerCvTemplatesItems.slice(
    activeIndexCvTemplates,
    activeIndexCvTemplates + visibleCount,
  );

  const allCareerProgressionItems = career_advice_helper
    .fetch({ limit: 0, featured: false })
    .filter((item) => hasCategory(item, "Career Progression"))
    .sort((a, b) => getDateValue(b) - getDateValue(a))
    .slice(0, md ? itemsCount : mobileCount);

  const CareerProgressionItemsToShow = allCareerProgressionItems.slice(
    activeIndexProgression,
    activeIndexProgression + visibleCount,
  );

  const allCareerSupportItems = career_advice_helper
    .fetch({ limit: 0, featured: false })
    .filter((item) => hasCategory(item, "Career Support"))
    .sort((a, b) => getDateValue(b) - getDateValue(a))
    .slice(0, md ? itemsCount : mobileCount);

  const CareerSupportItemsToShow = allCareerSupportItems.slice(activeIndexSupport, activeIndexSupport + visibleCount);

  const allEmploymentRightsItems = career_advice_helper
    .fetch({ limit: 0, featured: false })
    .filter((item) => hasCategory(item, "Employment Rights"))
    .sort((a, b) => getDateValue(b) - getDateValue(a))
    .slice(0, md ? itemsCount : mobileCount);

  const EmploymentRightsItemsToShow = allEmploymentRightsItems.slice(
    activeIndexRights,
    activeIndexRights + visibleCount,
  );

  // const allCareerJobDescriptionsItems = career_advice_helper
  //   .fetch({ limit: 0, featured: false })
  //   .filter((item) => item.related_category?.includes("Job Descriptions"))
  //   .sort((a, b) => new Date(b.date) - new Date(a.date))
  //   .slice(0, md ? itemsCount : mobileCount);
  // const CareerJobDescriptionsItemsToShow = allCareerJobDescriptionsItems.slice(
  //   activeIndexJobDescriptions,
  //   activeIndexJobDescriptions + visibleCount,
  // );

  // const allCareerCvTemplatesItems = career_advice_helper
  //   .fetch({ limit: 0, featured: false })
  //   .filter((item) => item.related_category?.includes("CV Templates"))
  //   .sort((a, b) => new Date(b.date) - new Date(a.date))
  //   .slice(0, md ? itemsCount : mobileCount);
  // const CareerCvTemplatesItemsToShow = allCareerCvTemplatesItems.slice(
  //   activeIndexCvTemplates,
  //   activeIndexCvTemplates + visibleCount,
  // );

  // const allCareerProgressionItems = career_advice_helper
  //   .fetch({ limit: 0, featured: false })
  //   .filter((item) => item.related_category === "Career Progression")
  //   .sort((a, b) => new Date(b.date) - new Date(a.date))
  //   .slice(0, md ? itemsCount : mobileCount);
  // const CareerProgressionItemsToShow = allCareerProgressionItems.slice(
  //   activeIndexProgression,
  //   activeIndexProgression + visibleCount,
  // );

  // const getDateValue = (item) => {
  //   if (!item.date) return 0;

  //   const parsed = new Date(item.date);
  //   return isNaN(parsed) ? 0 : parsed.getTime();
  // };

  // const allCareerProgressionItems = career_advice_helper
  //   .fetch({ limit: 0, featured: false })
  //   .filter((item) => {
  //     const raw = item.related_category;

  //     const category = Array.isArray(raw)
  //       ? raw[0]
  //       : typeof raw === "object" && raw !== null
  //       ? raw.name || raw.label || raw.value
  //       : raw;

  //     return typeof category === "string" && category.trim().toLowerCase() === "career progression";
  //   })
  //   .sort((a, b) => getDateValue(b) - getDateValue(a))
  //   .slice(0, md ? itemsCount : mobileCount);

  // const CareerProgressionItemsToShow = allCareerProgressionItems.slice(
  //   activeIndexProgression,
  //   activeIndexProgression + visibleCount,
  // );

  // const allCareerSupportItems = career_advice_helper
  //   .fetch({ limit: 0, featured: false })
  //   .filter((item) => item.related_category === "Career Support")
  //   .sort((a, b) => new Date(b.date) - new Date(a.date))
  //   .slice(0, md ? itemsCount : mobileCount);
  // const CareerSupportItemsToShow = allCareerSupportItems.slice(activeIndexSupport, activeIndexSupport + visibleCount);

  // const allEmploymentRightsItems = career_advice_helper
  //   .fetch({ limit: 0, featured: false })
  //   .filter((item) => item.related_category === "Employment Rights")
  //   .sort((a, b) => new Date(b.date) - new Date(a.date))
  //   .slice(0, md ? itemsCount : mobileCount);
  // const EmploymentRightsItemsToShow = allEmploymentRightsItems.slice(
  //   activeIndexRights,
  //   activeIndexRights + visibleCount,
  // );

  if (
    items
      .map((i) => i.title)
      .join()
      .trim().length === 0
  ) {
    return null;
  }

  return (
    <div className={clsx(className, classes.section)}>
      <Container className="mw-xxl">
        <Stack className={clsx(classes.filters, "p-4 mb-5")}>
          <Row>
            <Col xs={12} md className="mb-3 mb-md-0">
              <FormGroup className="position-relative">
                <FormLabel>Keywords</FormLabel>
                <FormControl
                  className="mt-2"
                  value={searchInput ?? ""}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onBlur={(e) => {
                    // Comment out or modify the onBlur logic if you don't want to clear the input immediately
                    // setSearchInput("")
                  }}
                  placeholder="Search..."
                />

                {searchResults.length > 0 && searchInput.length > 0 && (
                  <div className="position-absolute z-1 d-flex flex-column bg-light rounded p-4 mt-2">
                    {searchResults.slice(0, 8).map((result) => (
                      <a
                        key={result.url_slug}
                        href={(() => {
                          let slug = result.url_slug || "";
                          slug = slug.replace(/^job-descriptions\//i, ""); // remove leading "job-descriptions/"
                          slug = slug.replace(/^cv-templates\//i, ""); // optional – prevents same issue for CV Templates if ever nested

                          const specialCats = [
                            "CV Templates",
                            "Career Support",
                            "Career Progression",
                            "Employment Rights",
                            "Job Descriptions",
                            "Catering",
                            "Driving",
                            "EDI",
                            "Employment Law",
                            "Labour Insights",
                            "Working Rights",
                            "Workforce Planning",
                            "Warehouse",
                            "Training",
                            "Reports",
                          ];

                          const rawCat = result.related_category;

                          const catStr = Array.isArray(rawCat)
                            ? rawCat[0]
                            : rawCat && typeof rawCat === "object"
                            ? rawCat.name || rawCat.label || rawCat.value
                            : rawCat;

                          const normalized = typeof catStr === "string" ? catStr.trim().toLowerCase() : "";

                          if (normalized) {
                            const specialsLower = specialCats.map((c) => c.toLowerCase());
                            const matchIndex = specialsLower.indexOf(normalized);
                            if (matchIndex !== -1) {
                              const catSlug = specialsLower[matchIndex].replace(/\s+/g, "-");
                              return `/career-advice/${catSlug}/${slug}`;
                            }
                          }

                          return `/career-advice/${slug}`;
                        })()}
                        className="text-tertiary py-2 link-underline link-underline-opacity-0"
                      >
                        {result.title}
                      </a>
                    ))}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col xs={12} md>
              <Dropdown>
                <Dropdown.Header>Categories</Dropdown.Header>
                <Dropdown.Toggle className="mt-2" variant="white">
                  {selectTopic ? selectTopic : "Select Category"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {categories.map((c, k) => (
                    <Dropdown.Item
                      key={k}
                      href={`/career-advice/${c.toLowerCase().replace(/\s+/g, "-")}`}
                      // href={
                      //   c === "CV Templates"
                      //     ? "/career-advice/cv-templates"
                      //     : "/" + c.replace(" ", "-").toLocaleLowerCase()
                      // }
                    >
                      {c}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Stack>

        <div>
          <p className="fs-3 fw-bold text-center pb-4">Latest Career Advice</p>
          <Row className="mx-0">
            <Row className="mx-0">
              {oneItemPerCategory[activeIndex] && (
                <a
                  // href={oneItemPerCategory[activeIndex].url_slug ? oneItemPerCategory[activeIndex].url_slug : "/"}
                  // href={`${(oneItemPerCategory[activeIndex].related_category ?? "")
                  //   .toLowerCase()
                  //   .replace(/\s+/g, "-")}/${oneItemPerCategory[activeIndex].url_slug ?? ""}`}
                  // href={
                  //   oneItemPerCategory[activeIndex].related_category == "CV Templates"
                  //     ? getRoute("careerAdviceArticle", {
                  //         url_slug: `cv-templates/${oneItemPerCategory[activeIndex].url_slug}`,
                  //       })
                  //     : getRoute("careerAdviceArticle", { url_slug: oneItemPerCategory[activeIndex].url_slug })
                  // }
                  href={(() => {
                    const cat = oneItemPerCategory[activeIndex].related_category;
                    const slug = oneItemPerCategory[activeIndex].url_slug;
                    const specialCats = ["CV Templates", "Career Support", "Career Progression", "Employment Rights"];

                    if (specialCats.includes(cat)) {
                      const catSlug = cat.toLowerCase().replace(/\s+/g, "-");
                      return getRoute("careerAdviceArticle", { url_slug: `${catSlug}/${slug}` });
                    }

                    return getRoute("careerAdviceArticle", { url_slug: slug });
                  })()}
                  className={clsx(
                    classes.career_advice_section,
                    "link-dark link-offset-3-hover link-underline link-underline-opacity-0 text-black rounded-5 overflow-hidden",
                  )}
                >
                  <Row>
                    <Col md={6} className="p-0">
                      <img
                        src={oneItemPerCategory[activeIndex].card_image ?? null}
                        alt={oneItemPerCategory[activeIndex].card_image ?? null}
                        className="object-fit-cover w-100 h-100"
                      />
                    </Col>
                    <Col md={6} className="d-flex flex-column p-4 p-md-5" style={{ backgroundColor: "#F2F2F2" }}>
                      <p className="fs-3 fw-bold pb-4 title">{oneItemPerCategory[activeIndex].title}</p>
                      <div className="d-flex align-items-center">
                        <p>
                          {trimText(
                            oneItemPerCategory[activeIndex].card_summary &&
                              oneItemPerCategory[activeIndex].card_summary !== ""
                              ? oneItemPerCategory[activeIndex].card_summary
                              : oneItemPerCategory[activeIndex].content,
                          )}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </a>
              )}
            </Row>
          </Row>
          <Row className="my-md-4 mx-2 gap-4 align-items-center">
            <Col md />
            <Col md className="d-flex justify-content-center">
              {oneItemPerCategory.map((_, index) => (
                <span
                  key={index}
                  className={index === activeIndex ? `${styles.dot} ${styles.active}` : styles.dot}
                  onClick={() => setActiveIndex(index)}
                />
              ))}
            </Col>
            <Col md className="d-flex justify-content-center justify-content-md-end gap-2">
              <button className={`${styles.roundButton}`} onClick={() => handleNavClick("prev")}>{`<`}</button>
              <button className={`${styles.roundButton}`} onClick={() => handleNavClick("next")}>{`>`}</button>
            </Col>
          </Row>
          <hr className="mt-3" />
        </div>

        <Row>
          <div className="d-flex gap-4 align-items-center justify-content-between justify-content-md-start my-4">
            <p className="fs-4 fw-medium mb-1">Job Descriptions</p>
            <a href="/career-advice/job-descriptions">
              <button className="btn btn-primary " style={{ height: "28px", padding: "0 20px" }}>
                View all
              </button>
            </a>
          </div>
          {CareerJobDescriptionsItemsToShow.map((item, k) => (
            <Col key={k} xs={12} md={6} lg={4} xl={3}>
              <PromoItem
                className="h-100"
                title={item.title}
                description={trimText(item.card_summary && item.card_summary !== "" ? item.card_summary : item.content)}
                img={item.card_image ?? null}
                href={getRoute("careerAdviceArticle", { url_slug: item.url_slug }).replace(/\/$/, "")}
                cta={item.cta}
                external={item.external}
              />
            </Col>
          ))}
          {allCareerJobDescriptionsItems.length > visibleCount && (
            <Row className="my-md-4 mx-2 gap-4 align-items-center">
              <Col md />
              <Col md className="d-flex justify-content-center">
                {Array.from({ length: Math.ceil(allCareerJobDescriptionsItems.length / visibleCount) }, (_, index) => (
                  <span
                    key={index}
                    className={
                      index === activeIndexJobDescriptions / visibleCount
                        ? `${styles.dot} ${styles.active}`
                        : styles.dot
                    }
                    onClick={() => setActiveIndexJobDescriptions(index * visibleCount)}
                  />
                ))}
              </Col>
              <Col md className="d-flex justify-content-center justify-content-md-end gap-2">
                <button className={`${styles.roundButton}`} onClick={() => handleNavClickJobDescriptions("prev")}>
                  {`<`}
                </button>
                <button className={`${styles.roundButton}`} onClick={() => handleNavClickJobDescriptions("next")}>
                  {`>`}
                </button>
              </Col>
            </Row>
          )}

          <hr className="mt-3" />
        </Row>

        <Row>
          <div className="d-flex gap-4 align-items-center justify-content-between justify-content-md-start my-4">
            <p className="fs-4 fw-medium mb-1">CV Templates</p>
            <a href="/career-advice/cv-templates">
              <button className="btn btn-primary " style={{ height: "28px", padding: "0 20px" }}>
                View all
              </button>
            </a>
          </div>
          {CareerCvTemplatesItemsToShow.map((item, k) => (
            <Col key={k} xs={12} md={6} lg={4} xl={3}>
              <PromoItem
                className="h-100"
                title={item.title}
                description={trimText(item.card_summary && item.card_summary !== "" ? item.card_summary : item.content)}
                img={item.card_image ?? null}
                // href={getRoute("careerAdviceArticle", { url_slug: item.url_slug })}
                href={getRoute("careerAdviceArticle", { url_slug: `cv-templates/${item.url_slug}` })}
                cta={item.cta}
                external={item.external}
              />
            </Col>
          ))}
          {allCareerCvTemplatesItems.length > visibleCount && (
            <Row className="my-md-4 mx-2 gap-4 align-items-center">
              <Col md />
              <Col md className="d-flex justify-content-center">
                {allCareerCvTemplatesItems.slice(0, allCareerCvTemplatesItems.length / visibleCount).map((_, index) => (
                  <span
                    key={index}
                    className={
                      index === activeIndexCvTemplates / visibleCount ? `${styles.dot} ${styles.active}` : styles.dot
                    }
                    onClick={() => setActiveIndexCvTemplates(index)}
                  />
                ))}
              </Col>
              <Col md className="d-flex justify-content-center justify-content-md-end gap-2">
                <button
                  className={`${styles.roundButton}`}
                  onClick={() => handleNavClickCvTemplates("prev")}
                >{`<`}</button>
                <button
                  className={`${styles.roundButton}`}
                  onClick={() => handleNavClickCvTemplates("next")}
                >{`>`}</button>
              </Col>
            </Row>
          )}
          <hr className="mt-3" />
        </Row>

        <Row>
          <div className="d-flex gap-4 align-items-center justify-content-between justify-content-md-start my-4">
            <p className="fs-4 fw-medium mb-1">Career Progression</p>
            <a href="/career-advice/career-progression">
              <button className="btn btn-primary " style={{ height: "28px", padding: "0 20px" }}>
                View all
              </button>
            </a>
          </div>
          {CareerProgressionItemsToShow.map((item, k) => (
            <Col key={k} xs={12} md={6} lg={4} xl={3}>
              <PromoItem
                className="h-100"
                title={item.title}
                description={trimText(item.card_summary && item.card_summary !== "" ? item.card_summary : item.content)}
                img={item.card_image ?? null}
                // href={getRoute("careerAdviceArticle", { url_slug: item.url_slug })}
                href={getRoute("careerAdviceArticle", { url_slug: `career-progression/${item.url_slug}` })}
                cta={item.cta}
                external={item.external}
              />
            </Col>
          ))}
          {allCareerProgressionItems.length > visibleCount && (
            <Row className="my-md-4 mx-2 gap-4 align-items-center">
              <Col md />
              <Col md className="d-flex justify-content-center">
                {allCareerProgressionItems.slice(0, allCareerProgressionItems.length / visibleCount).map((_, index) => (
                  <span
                    key={index}
                    className={
                      index === activeIndexProgression / visibleCount ? `${styles.dot} ${styles.active}` : styles.dot
                    }
                    onClick={() => setActiveIndexProgression(index)}
                  />
                ))}
              </Col>
              <Col md className="d-flex justify-content-center justify-content-md-end gap-2">
                <button
                  className={`${styles.roundButton}`}
                  onClick={() => handleNavClickProgression("prev")}
                >{`<`}</button>
                <button
                  className={`${styles.roundButton}`}
                  onClick={() => handleNavClickProgression("next")}
                >{`>`}</button>
              </Col>
            </Row>
          )}
          <hr className="mt-3" />
        </Row>

        <Row>
          <div className="d-flex gap-4 align-items-center justify-content-between justify-content-md-start my-4">
            <p className="fs-4 fw-medium mb-1">Career Support</p>
            <a href="/career-advice/career-support">
              <button className="btn btn-primary " style={{ height: "28px", padding: "0 20px" }}>
                View all
              </button>
            </a>
          </div>
          {CareerSupportItemsToShow.map((item, k) => (
            <Col key={k} xs={12} md={6} lg={4} xl={3}>
              <PromoItem
                className="h-100"
                title={item.title}
                description={trimText(item.card_summary && item.card_summary !== "" ? item.card_summary : item.content)}
                img={item.card_image ?? null}
                // href={getRoute("careerAdviceArticle", { url_slug: item.url_slug })}
                href={getRoute("careerAdviceArticle", { url_slug: `career-support/${item.url_slug}` })}
                cta={item.cta}
                external={item.external}
              />
            </Col>
          ))}
          {allCareerSupportItems.length > visibleCount && (
            <Row className="my-md-4 mx-2 gap-4 align-items-center">
              <Col md />
              <Col md className="d-flex justify-content-center">
                {allCareerSupportItems.slice(0, allCareerSupportItems.length / visibleCount).map((_, index) => (
                  <span
                    key={index}
                    className={
                      index === activeIndexSupport / visibleCount ? `${styles.dot} ${styles.active}` : styles.dot
                    }
                    onClick={() => setActiveIndexSupport(index)}
                  />
                ))}
              </Col>
              <Col md className="d-flex justify-content-center justify-content-md-end gap-2">
                <button className={`${styles.roundButton}`} onClick={() => handleNavClickSupport("prev")}>{`<`}</button>
                <button className={`${styles.roundButton}`} onClick={() => handleNavClickSupport("next")}>{`>`}</button>
              </Col>
            </Row>
          )}
          <hr className="mt-3" />
        </Row>

        <Row className="mb-5">
          <div className="d-flex gap-4 align-items-center justify-content-between justify-content-md-start my-4">
            <p className="fs-4 fw-medium mb-1">Employment Rights</p>
            <a href="/career-advice/employment-rights">
              <button className="btn btn-primary " style={{ height: "28px", padding: "0 20px" }}>
                View all
              </button>
            </a>
          </div>
          {EmploymentRightsItemsToShow.map((item, k) => (
            <Col key={k} xs={12} md={6} lg={4} xl={3}>
              <PromoItem
                className="h-100"
                title={item.title}
                description={trimText(item.card_summary && item.card_summary !== "" ? item.card_summary : item.content)}
                img={item.card_image ?? null}
                // href={getRoute("careerAdviceArticle", { url_slug: item.url_slug })}
                href={getRoute("careerAdviceArticle", { url_slug: `employment-rights/${item.url_slug}` })}
                cta={item.cta}
                external={item.external}
              />
            </Col>
          ))}
          {allEmploymentRightsItems.length > visibleCount && (
            <Row className="my-md-4 mx-2 gap-4 align-items-center">
              <Col md />
              <Col md className="d-flex justify-content-center">
                {allEmploymentRightsItems.slice(0, allEmploymentRightsItems.length / visibleCount).map((_, index) => (
                  <span
                    key={index}
                    className={
                      index === activeIndexRights / visibleCount ? `${styles.dot} ${styles.active}` : styles.dot
                    }
                    onClick={() => setActiveIndexRights(index)}
                  />
                ))}
              </Col>
              <Col md className="d-flex justify-content-center justify-content-md-end gap-2">
                <button className={`${styles.roundButton}`} onClick={() => handleNavClickRights("prev")}>{`<`}</button>
                <button className={`${styles.roundButton}`} onClick={() => handleNavClickRights("next")}>{`>`}</button>
              </Col>
            </Row>
          )}
        </Row>
      </Container>
    </div>
  );
}

CareerAdviceContent.propTypes = {
  className: PropTypes.string,
  title: PropTypes.any,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      className: PropTypes.string,
      title: PropTypes.string,
      img: PropTypes.string,
      description: PropTypes.string,
      href: PropTypes.string,
    }),
  ),
};
